
import { useState, useEffect } from 'react';
import { ActiveCampaign, CampaignSettings, CampaignReport } from '@/components/admin/campaigns/types';
import { generateCampaignId } from '@/utils/campaignUtils';

export const useCampaigns = () => {
  const [campaigns, setCampaigns] = useState<ActiveCampaign[]>([]);
  const [campaignReports, setCampaignReports] = useState<CampaignReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // In a real app, this would fetch from an API
    // For now, let's simulate loading
    setLoading(true);
    
    setTimeout(() => {
      // Mock data
      const mockCampaigns: ActiveCampaign[] = [
        {
          id: 'campaign-1',
          campaignId: 'campaign-1',
          globalCampaignId: 'CPN-A1B2C3D4', // Added global campaign ID
          subType: 'double',
          title: 'Holiday Double Referral',
          subtitle: 'Give $25, Get $25 when you refer friends',
          referrerReward: 25,
          refereeReward: 25,
          currency: 'USD',
          shareOptions: ['facebook', 'twitter', 'whatsapp', 'mail'],
          showAffiliatePortal: true,
          customMessages: {},
          collectPhoneNumber: false,
          limitReferrals: false,
          showTerms: false,
          termsContent: '',
          programDescription: 'Holiday promotion',
          rewardInfo: '',
          status: 'active',
          startDate: new Date(2025, 3, 15),
          referralCount: 132,
          conversionRate: 0.24,
          totalRewards: 3300,
          isActive: true,
          createdAt: new Date(2025, 3, 15),
          updatedAt: new Date(2025, 3, 15)
        },
        {
          id: 'campaign-2',
          campaignId: 'campaign-2',
          globalCampaignId: 'CPN-E5F6G7H8', // Added global campaign ID
          subType: 'single',
          title: 'Early Bird Referral',
          subtitle: 'Get $15 for every friend you refer',
          referrerReward: 15,
          refereeReward: 0,
          currency: 'USD',
          shareOptions: ['facebook', 'twitter', 'instagram', 'mail'],
          showAffiliatePortal: false,
          customMessages: {},
          collectPhoneNumber: true,
          limitReferrals: true,
          showTerms: true,
          termsContent: 'Terms and conditions apply',
          programDescription: 'Early access promotion',
          rewardInfo: '',
          status: 'paused',
          startDate: new Date(2025, 2, 1),
          referralCount: 87,
          conversionRate: 0.18,
          totalRewards: 1305,
          isActive: false,
          createdAt: new Date(2025, 2, 1),
          updatedAt: new Date(2025, 3, 10)
        }
      ];
      
      // Generate mock campaign reports based on campaigns
      const mockReports: CampaignReport[] = mockCampaigns.map(camp => ({
        id: `report-${camp.id}`,
        globalCampaignId: camp.globalCampaignId || 'CPN-00000000',
        adminId: 'admin-123',
        superAdminId: 'super-admin-456',
        campaignName: camp.title,
        startDate: camp.startDate,
        endDate: camp.status === 'completed' ? new Date() : undefined,
        totalReferrals: camp.referralCount,
        successfulReferrals: Math.floor(camp.referralCount * camp.conversionRate),
        conversionRate: camp.conversionRate,
        totalRewards: camp.totalRewards,
        status: camp.status,
        createdAt: camp.createdAt || new Date(),
        updatedAt: camp.updatedAt || new Date()
      }));
      
      setCampaigns(mockCampaigns);
      setCampaignReports(mockReports);
      setLoading(false);
    }, 500);
  }, []);

  const createCampaign = (settings: CampaignSettings): Promise<ActiveCampaign> => {
    return new Promise((resolve) => {
      // Generate a unique global campaign ID with CPN prefix
      const globalCampaignId = settings.globalCampaignId || generateCampaignId();
      
      // In a real app, this would be an API call
      const newCampaign: ActiveCampaign = {
        ...settings,
        id: settings.campaignId || `campaign-${Date.now()}`,
        campaignId: settings.campaignId || `campaign-${Date.now()}`,
        globalCampaignId, // Set the global campaign ID
        status: 'active',
        startDate: new Date(),
        referralCount: 0,
        conversionRate: 0,
        totalRewards: 0,
        isActive: true,
        createdAt: settings.createdAt || new Date(),
        updatedAt: new Date()
      };
      
      // Create corresponding campaign report
      const newReport: CampaignReport = {
        id: `report-${newCampaign.id}`,
        globalCampaignId,
        adminId: 'admin-123', // In a real app, this would be the current admin user ID
        superAdminId: 'super-admin-456', // In a real app, this would be fetched from the admin's parent
        campaignName: newCampaign.title,
        startDate: newCampaign.startDate,
        totalReferrals: 0,
        successfulReferrals: 0,
        conversionRate: 0,
        totalRewards: 0,
        status: newCampaign.status,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Add to state
      setCampaigns(prev => [newCampaign, ...prev]);
      setCampaignReports(prev => [newReport, ...prev]);
      
      // Simulate API delay
      setTimeout(() => {
        resolve(newCampaign);
      }, 500);
    });
  };

  const updateCampaign = (id: string, updates: Partial<ActiveCampaign>): Promise<ActiveCampaign> => {
    return new Promise((resolve, reject) => {
      const campaignIndex = campaigns.findIndex(c => c.id === id);
      
      if (campaignIndex === -1) {
        reject(new Error('Campaign not found'));
        return;
      }
      
      const updatedCampaign = {
        ...campaigns[campaignIndex],
        ...updates,
        updatedAt: new Date()
      };
      
      const newCampaigns = [...campaigns];
      newCampaigns[campaignIndex] = updatedCampaign;
      
      setCampaigns(newCampaigns);
      
      // Also update the corresponding report
      const reportIndex = campaignReports.findIndex(r => r.globalCampaignId === updatedCampaign.globalCampaignId);
      if (reportIndex !== -1) {
        const updatedReport = {
          ...campaignReports[reportIndex],
          campaignName: updatedCampaign.title,
          status: updatedCampaign.status,
          totalReferrals: updatedCampaign.referralCount,
          successfulReferrals: Math.floor(updatedCampaign.referralCount * updatedCampaign.conversionRate),
          conversionRate: updatedCampaign.conversionRate,
          totalRewards: updatedCampaign.totalRewards,
          updatedAt: new Date(),
          endDate: updatedCampaign.status === 'completed' ? new Date() : undefined
        };
        
        const newReports = [...campaignReports];
        newReports[reportIndex] = updatedReport;
        setCampaignReports(newReports);
      }
      
      // Simulate API delay
      setTimeout(() => {
        resolve(updatedCampaign);
      }, 500);
    });
  };

  const toggleCampaignStatus = async (id: string): Promise<ActiveCampaign> => {
    const campaign = campaigns.find(c => c.id === id);
    
    if (!campaign) {
      throw new Error('Campaign not found');
    }
    
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    
    return updateCampaign(id, {
      status: newStatus,
      isActive: newStatus === 'active'
    });
  };

  // Get campaign reports by admin ID or super admin ID
  const getCampaignReportsByAdmin = (adminId: string) => {
    return campaignReports.filter(report => report.adminId === adminId);
  };

  const getCampaignReportsBySuperAdmin = (superAdminId: string) => {
    return campaignReports.filter(report => report.superAdminId === superAdminId);
  };

  // Get a campaign report by global campaign ID
  const getCampaignReportByGlobalId = (globalId: string) => {
    return campaignReports.find(report => report.globalCampaignId === globalId);
  };

  return {
    campaigns,
    campaignReports,
    loading,
    error,
    createCampaign,
    updateCampaign,
    toggleCampaignStatus,
    getCampaignReportsByAdmin,
    getCampaignReportsBySuperAdmin,
    getCampaignReportByGlobalId
  };
};

export default useCampaigns;
